import { computed, Injectable, Signal, signal } from '@angular/core';

/* Used to represent the entire app's loading state */

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  /**
   * Internal set of “in-flight” identifiers (URL, cache key, etc.)
   */
  readonly #pending = signal<Set<string>>(new Set());

  /** `true` while **any** request is loading. */
  readonly loading = computed(() => this.#pending().size > 0);

  /** Start tracking a request. */
  start(key: string): void {
    this.#pending.update(set => new Set(set).add(key));
  }

  /** Stop tracking a request. Safe to call even if the key isn’t present. */
  finish(key: string): void {
    this.#pending.update(set => {
      const next = new Set(set);
      next.delete(key);
      return next;
    });
  }

  /**
   * Reactive check: returns a **Signal<boolean>** that updates automatically
   * whenever the loading state for this key changes.
   */
  isLoading(key: string): Signal<boolean> {
    return computed(() => this.#pending().has(key));
  }

  /**
   * Synchronous snapshot (non-reactive). Handy if you just need a boolean once.
   */
  isLoadingNow(key: string): boolean {
    return this.#pending().has(key);
  }
}
